<template>
  <div class="order-statistics-container">
    <h2 class="page-title">订单交易统计</h2>
    
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-item">
            <div class="stat-icon total">
              <i class="el-icon-s-order"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">总交易笔数</div>
              <div class="stat-value">{{ statistics.totalCount }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-item">
            <div class="stat-icon success">
              <i class="el-icon-success"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">成功交易</div>
              <div class="stat-value">{{ statistics.successCount }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-item">
            <div class="stat-icon refund">
              <i class="el-icon-refund"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">退款交易</div>
              <div class="stat-value">{{ statistics.refundCount }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-item">
            <div class="stat-icon amount">
              <i class="el-icon-money"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">总交易金额</div>
              <div class="stat-value">¥ {{ statistics.totalAmount.toFixed(2) }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 新增：交易趋势折线图 (按日期汇总) -->
    <el-row :gutter="20" class="chart-row">
      <el-col :span="24">
        <el-card class="chart-card" shadow="hover">
          <div slot="header" class="card-header">
            <span>交易趋势分析 (近30天)</span>
            <el-radio-group v-model="trendType" size="small" @change="updateTrendChart">
              <el-radio-button label="amount">金额趋势</el-radio-button>
              <el-radio-button label="count">笔数趋势</el-radio-button>
            </el-radio-group>
          </div>
          <div id="trendChart" style="width: 100%; height: 350px;"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 交易类型 & 状态分布 (双饼图) -->
    <el-row :gutter="20" class="chart-row">
      <el-col :span="12">
        <el-card class="chart-card" shadow="hover">
          <div slot="header" class="card-header">
            <span>交易类型分布</span>
          </div>
          <div id="typeChart" style="width: 100%; height: 300px;"></div>
        </el-card>
      </el-col>
      
      <el-col :span="12">
        <el-card class="chart-card" shadow="hover">
          <div slot="header" class="card-header">
            <span>交易状态分布</span>
          </div>
          <div id="statusChart" style="width: 100%; height: 300px;"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 交易列表 -->
    <el-card class="table-card" shadow="hover">
      <div slot="header" class="card-header">
        <span>交易记录列表</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="yyyy-MM-dd"
          value-format="yyyyMMdd"
          @change="filterByDate"
          size="small"
        >
        </el-date-picker>
      </div>
      
      <el-table 
        :data="filteredOrders" 
        style="width: 100%"
        :max-height="500"
        stripe
        border
      >
        <el-table-column type="index" label="序号" width="60"></el-table-column>
        <el-table-column prop="fasSettleDt" label="结算日期" width="100">
          <template #default="scope">
            {{ formatDate(scope.row.fasSettleDt) }}
          </template>
        </el-table-column>
        <el-table-column prop="gtwBusiCdDesc" label="交易类型" width="120">
          <template #default="scope">
            <el-tag :type="getTxnTypeTag(scope.row.gtwBusiCdDesc)">
              {{ scope.row.gtwBusiCdDesc }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="insNameCn" label="商户名称" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="srcTxnAmt" label="交易金额" width="120">
          <template #default="scope">
            <span :class="{ 'refund-amount': scope.row.destTxnAmt < 0 }">
              ¥ {{ formatAmount(scope.row.srcTxnAmt) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="settleAmt" label="结算金额" width="120">
          <template #default="scope">
            <span :class="{ 'refund-amount': scope.row.settleAmt < 0 }">
              ¥ {{ formatAmount(scope.row.settleAmt || scope.row.destTxnAmt) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="bookMdDesc" label="交易状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusTag(scope.row.bookMdDesc)" size="small">
              {{ scope.row.bookMdDesc }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="refundAmt" label="退款金额" width="120">
          <template #default="scope">
            <span v-if="scope.row.refundAmt && scope.row.refundAmt > 0" class="refund-amount">
              ¥ {{ formatAmount(scope.row.refundAmt) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="fasTraceNo" label="交易流水号" width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="termShopId" label="店铺名称" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="resrvChr1" label="订单号" width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="reserved" label="备注" min-width="150" show-overflow-tooltip></el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredOrders.length"
        class="pagination"
      >
      </el-pagination>
    </el-card>
  </div>
</template>

<script>
import axios from "axios";
import * as echarts from 'echarts';

export default {
  name: "OrderStatistics",
  
  data() {
    return {
      // 基础配置
      baseUrl: 'http://localhost:8090', // 后端接口前缀
      
      // 数据
      orders: [], // 所有订单数据
      
      // 统计信息
      statistics: {
        totalCount: 0,
        successCount: 0,
        refundCount: 0,
        totalAmount: 0
      },
      
      // 交易类型统计
      txnTypeStats: {},
      txnStatusStats: {},
      
      // 趋势图类型: amount / count
      trendType: 'amount',
      
      // 筛选
      dateRange: [],
      
      // 分页
      currentPage: 1,
      pageSize: 20
    };
  },
  
  computed: {
    // 筛选后的订单
    filteredOrders() {
      let filtered = [...this.orders];
      
      // 日期筛选
      if (this.dateRange && this.dateRange.length === 2) {
        const startDate = this.dateRange[0];
        const endDate = this.dateRange[1];
        filtered = filtered.filter(item => 
          item.fasSettleDt >= startDate && item.fasSettleDt <= endDate
        );
      }
      
      // 分页
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return filtered.slice(start, end);
    }
  },
  
  mounted() {
    this.fetchOrders();
    this.$nextTick(() => {
      window.addEventListener('resize', this.handleResize);
    });
  },
  
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.typeChart) this.typeChart.dispose();
    if (this.statusChart) this.statusChart.dispose();
    if (this.trendChart) this.trendChart.dispose();
  },
  
  methods: {
    // 获取所有交易记录
    fetchOrders() {
      axios.get("http://localhost:8090/user/getAllTXN")
        .then(response => {
          if (response.data.code) {
            this.orders = response.data.data || [];
            this.calculateStatistics();
            this.$nextTick(() => {
              this.initCharts();
              this.initTrendChart(); // 新增趋势图初始化
            });
          } else {
            this.$message.error('获取交易记录失败');
          }
        })
        .catch(error => {
          console.error('获取交易记录失败:', error);
          this.$message.error('网络错误，无法获取交易记录');
        });
    },
    
    // 计算统计信息
    calculateStatistics() {
      let totalAmount = 0;
      let successCount = 0;
      let refundCount = 0;
      let typeStats = {};
      let statusStats = {};
      
      this.orders.forEach(order => {
        // 交易金额统计
        const amount = parseFloat(order.settleAmt || order.destTxnAmt || 0);
        totalAmount += amount;
        
        // 状态统计
        if (order.bookMdDesc === '成功') {
          successCount++;
        } else if (order.bookMdDesc === '全部退款' || (order.bookMdDesc && order.bookMdDesc.includes('退款'))) {
          refundCount++;
        }
        
        // 交易类型统计
        const txnType = order.gtwBusiCdDesc || '未知类型';
        typeStats[txnType] = (typeStats[txnType] || 0) + 1;
        
        // 交易状态统计
        const status = order.bookMdDesc || '未知状态';
        statusStats[status] = (statusStats[status] || 0) + 1;
      });
      
      this.statistics = {
        totalCount: this.orders.length,
        successCount,
        refundCount,
        totalAmount
      };
      
      this.txnTypeStats = typeStats;
      this.txnStatusStats = statusStats;
    },
    
    // ================== 折线图：按日期聚合交易金额/笔数 ==================
    getDateRangeData() {
      // 按fasSettleDt聚合
      const dateMap = new Map(); // key: yyyyMMdd, value: { amount: 0, count: 0 }
      
      this.orders.forEach(order => {
        const date = order.fasSettleDt;
        if (!date) return;
        
        const amount = parseFloat(order.settleAmt || order.destTxnAmt || 0);
        if (!dateMap.has(date)) {
          dateMap.set(date, { amount: 0, count: 0 });
        }
        const record = dateMap.get(date);
        record.amount += amount;
        record.count += 1;
      });
      
      // 转换为数组并按日期排序
      const sorted = Array.from(dateMap.entries())
        .map(([date, values]) => ({ date, ...values }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // 取最近30天 (或全部，这里取全部以便演示)
      return sorted;
    },
    
    initTrendChart() {
      const chartDom = document.getElementById('trendChart');
      if (!chartDom) return;
      this.trendChart = echarts.init(chartDom);
      this.updateTrendChart();
    },
    
    updateTrendChart() {
      if (!this.trendChart) return;
      
      const data = this.getDateRangeData();
      const dates = data.map(item => this.formatDate(item.date));
      
      let seriesData = [];
      let seriesName = '';
      let yAxisName = '';
      
      if (this.trendType === 'amount') {
        seriesData = data.map(item => item.amount.toFixed(2));
        seriesName = '交易金额 (¥)';
        yAxisName = '金额 (元)';
      } else {
        seriesData = data.map(item => item.count);
        seriesName = '交易笔数';
        yAxisName = '笔数';
      }
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: function(params) {
            return params[0].name + '<br/>' + params[0].marker + ' ' + params[0].seriesName + ': ' + params[0].value;
          }
        },
        grid: {
          left: '5%',
          right: '5%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: { rotate: 30, interval: Math.ceil(dates.length / 20) } // 防止标签重叠
        },
        yAxis: {
          type: 'value',
          name: yAxisName,
          nameTextStyle: { fontSize: 12 }
        },
        series: [
          {
            name: seriesName,
            type: 'line',
            data: seriesData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 3, color: '#5470c6' },
            areaStyle: { color: 'rgba(84,112,198,0.1)' },
            markPoint: {
              data: [
                { type: 'max', name: '最大值' },
                { type: 'min', name: '最小值' }
              ]
            }
          }
        ],
        dataZoom: [
          { type: 'slider', start: 0, end: 100 },
          { type: 'inside', start: 0, end: 100 }
        ]
      };
      
      this.trendChart.setOption(option);
    },
    
    // ================== 饼图 ==================
    initCharts() {
      // 交易类型分布图
      this.typeChart = echarts.init(document.getElementById('typeChart'));
      const typeOption = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}笔 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'center',
          textStyle: { fontSize: 12 }
        },
        series: [
          {
            name: '交易类型',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: { show: false },
            emphasis: {
              label: { show: true, fontSize: '12', fontWeight: 'bold' }
            },
            data: Object.entries(this.txnTypeStats).map(([name, value]) => ({
              name,
              value
            }))
          }
        ],
        color: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4']
      };
      this.typeChart.setOption(typeOption);
      
      // 交易状态分布图
      this.statusChart = echarts.init(document.getElementById('statusChart'));
      const statusOption = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}笔 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'center',
          textStyle: { fontSize: 12 }
        },
        series: [
          {
            name: '交易状态',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: { show: false },
            emphasis: {
              label: { show: true, fontSize: '12', fontWeight: 'bold' }
            },
            data: Object.entries(this.txnStatusStats).map(([name, value]) => ({
              name,
              value
            }))
          }
        ],
        color: ['#67c23a', '#e6a23c', '#f56c6c', '#909399', '#409eff']
      };
      this.statusChart.setOption(statusOption);
    },
    
    // 处理窗口大小变化
    handleResize() {
      if (this.typeChart) this.typeChart.resize();
      if (this.statusChart) this.statusChart.resize();
      if (this.trendChart) this.trendChart.resize();
    },
    
    // 日期筛选
    filterByDate() {
      this.currentPage = 1;
      // 可选：重新计算图表（因为折线图基于全部数据，这里不刷新也可以）
      // 如果想根据日期范围动态更新折线图，需要重新实现聚合逻辑，此处略。
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '';
      if (dateStr.length === 8) {
        return `${dateStr.substr(0,4)}-${dateStr.substr(4,2)}-${dateStr.substr(6,2)}`;
      }
      return dateStr;
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = parseFloat(amount);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    },
    
    // 获取交易类型标签样式
    getTxnTypeTag(type) {
      if (!type) return '';
      if (type.includes('退款')) return 'danger';
      if (type.includes('支付')) return 'success';
      if (type.includes('扫码')) return 'primary';
      return '';
    },
    
    // 获取状态标签样式
    getStatusTag(status) {
      if (status === '成功') return 'success';
      if (status === '全部退款') return 'danger';
      if (status && status.includes('退款')) return 'warning';
      return 'info';
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
    },
    
    // 页码变化
    handleCurrentChange(val) {
      this.currentPage = val;
    }
  }
};
</script>

<style scoped>
.order-statistics-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-title {
  text-align: center;
  margin-bottom: 30px;
  color: #303133;
  font-weight: bold;
  font-size: 28px;
  position: relative;
  padding-bottom: 15px;
}

.page-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #409eff, #67c23a);
  border-radius: 3px;
}

/* 统计卡片 */
.stat-cards {
  margin-bottom: 20px;
}

.stat-card {
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 20px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 32px;
  color: white;
}

.stat-icon.total {
  background: linear-gradient(135deg, #409eff, #53a8ff);
}

.stat-icon.success {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.refund {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stat-icon.amount {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

/* 图表卡片 */
.chart-row {
  margin-bottom: 20px;
}

.chart-card {
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #303133;
}

/* 表格卡片 */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

.refund-amount {
  color: #f56c6c;
  font-weight: bold;
}

/* 分页 */
.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 表格样式优化 */
::v-deep .el-table th {
  background-color: #f5f7fa;
  color: #303133;
  font-weight: bold;
}

::v-deep .el-table td {
  padding: 12px 0;
}

::v-deep .el-tag {
  border-radius: 4px;
}

/* 响应式 */
@media (max-width: 1200px) {
  .stat-cards .el-col {
    margin-bottom: 15px;
  }
  
  .chart-row .el-col {
    margin-bottom: 15px;
  }
}

@media (max-width: 768px) {
  .order-statistics-container {
    padding: 10px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .stat-value {
    font-size: 20px;
  }
}
</style>